import { useCallback, useEffect, useRef, useState } from 'react';

import { addMilliseconds, isAfter } from 'date-fns';
import { always, clone, isNil } from 'ramda';

import { GOOGLE_MAP_CONTAINER_CLASSNAME } from 'constants/index';
import { waitAndScrollToEement } from 'utils/scrollToMapContainer';

export function usePrint() {
  const canvasRef = useRef(null);

  const numeratePages = useCallback(() => {
    const pages = document.querySelectorAll('.pdfPage');
    if (pages) {
      pages.forEach((page, i) => {
        page.dataset.count = String(i + 1);
        page.dataset.allCount = String(pages?.length);
      });
    }
  }, []);

  const allImagesLoaded = useCallback(async () => {
    if (canvasRef.current !== null) {
      const images = Array.from(canvasRef.current.querySelectorAll('img[src]'));
      const isLoaded = (img) => img.complete;

      return await new Promise((res) => {
        const startTime = new Date();
        const endTime = addMilliseconds(startTime, 60_000);
        const interval = setInterval(() => {
          if (isAfter(new Date(), endTime)) {
            clearInterval(interval);
            res();
          }

          if (images.every(isLoaded)) {
            clearInterval(interval);
            res();
          }
        }, 1000);
      });
    }
  }, []);

  return {
    canvasRef,
    numeratePages,
    allImagesLoaded,
  };
}

export function useAllPagesReady({ config, onReady }) {
  const { showTravelBriefPage, showMapPage, showCostsPage } = config;
  const [coverReady, setCoverReady] = useState(false);
  const [pagesAfterCoverPageReady, setPagesAfterCoverPageReady] = useState(false);
  const [mapReady, setMapReady] = useState(!showMapPage);
  const [travelBriefReady, setTravelBriefReady] = useState(!showTravelBriefPage);
  const [emergencyContactsReady, setEmergencyContactsReady] = useState(false);
  const [pagesReady, setPagesReady] = useState(false);
  const [pagesAfterCostsPageReady, setPagesAfterCostsPageReady] = useState(false);
  const [costsReady, setCostsReady] = useState(!showCostsPage);

  useEffect(() => {
    console.time('Pages after cover ready in');
    console.time('Map ready in');
    console.time('Travel Brief rendering');
    console.time('Emergency Contacts ready in');
    console.time('Pages ready in');
    console.time('Pages after costs ready in');
    console.time('Costs ready in');
    console.time('Total time for rendering');
  }, []);

  useEffect(() => {
    console.log(
      '---- useAllPagesReady v2',
      coverReady &&
        pagesAfterCoverPageReady &&
        mapReady &&
        travelBriefReady &&
        emergencyContactsReady &&
        pagesReady &&
        pagesAfterCostsPageReady &&
        costsReady,
      '|',
      coverReady,
      pagesAfterCoverPageReady,
      mapReady,
      travelBriefReady,
      emergencyContactsReady,
      pagesReady,
      pagesAfterCostsPageReady,
      costsReady,
    );
    if (
      coverReady &&
      pagesAfterCoverPageReady &&
      mapReady &&
      travelBriefReady &&
      emergencyContactsReady &&
      pagesReady &&
      pagesAfterCostsPageReady &&
      costsReady
    ) {
      onReady();
      console.timeEnd('Total time for rendering');
    }
  }, [
    coverReady,
    pagesAfterCoverPageReady,
    mapReady,
    travelBriefReady,
    emergencyContactsReady,
    pagesReady,
    pagesAfterCostsPageReady,
    costsReady,
    onReady,
  ]);

  // need to memoize following functions in order to prevent unnecessary re-renders
  const handleSetCoverReady = useCallback(() => {
    setCoverReady(true);
  }, []);

  const handleSetPagesAfterCoverPageReady = useCallback(() => {
    setPagesAfterCoverPageReady(true);

    console.timeEnd('Pages after cover ready in');
  }, []);

  const handleSetMapReady = useCallback(() => {
    setMapReady(true);

    console.timeEnd('Map ready in');
  }, []);

  const handleSetTravelBriefReady = useCallback(() => {
    setTravelBriefReady(true);

    console.timeEnd('Travel Brief rendering');
  }, []);

  const handleSetEmergencyContactsReady = useCallback(() => {
    setEmergencyContactsReady(true);

    console.timeEnd('Emergency Contacts ready in');
  }, []);

  const handleSetPagesReady = useCallback(() => {
    setPagesReady(true);

    console.timeEnd('Pages ready in');
  }, []);

  const handleSetPagesAfterCostsPageReady = useCallback(() => {
    setPagesAfterCostsPageReady(true);

    console.timeEnd('Pages after costs ready in');
  }, []);

  const handleSetCostsReady = useCallback(() => {
    setCostsReady(true);

    console.timeEnd('Costs ready in');
  }, []);

  // this useEffect is used for scrolling to the google map container after all
  // pages will be rendered, bc google js api renders the map only
  // if the element is inside viewport
  useEffect(() => {
    if (
      coverReady &&
      pagesAfterCoverPageReady &&
      travelBriefReady &&
      emergencyContactsReady &&
      pagesReady &&
      pagesAfterCostsPageReady &&
      costsReady
    ) {
      waitAndScrollToEement(GOOGLE_MAP_CONTAINER_CLASSNAME, 15_000, handleSetMapReady);
    }
  }, [
    coverReady,
    pagesAfterCoverPageReady,
    travelBriefReady,
    emergencyContactsReady,
    pagesReady,
    pagesAfterCostsPageReady,
    costsReady,
  ]);

  return {
    setCoverReady: handleSetCoverReady,
    setPagesAfterCoverPageReady: handleSetPagesAfterCoverPageReady,
    setMapReady: handleSetMapReady,
    setTravelBriefReady: handleSetTravelBriefReady,
    setPagesReady: handleSetPagesReady,
    setPagesAfterCostsPageReady: handleSetPagesAfterCostsPageReady,
    setCostsReady: handleSetCostsReady,
    setEmergencyContactsReady: handleSetEmergencyContactsReady,
  };
}

export function useRecursive({ items, onReady, checkPageDevide = always(false) }) {
  const currentItemIndex = useRef(0);
  const currentPageIndex = useRef(0);
  const [innerPages, setInnerPages] = useState([[items[0]]]);

  const handleFail = useCallback(() => {
    const newInnerPages = clone(innerPages);
    const currentPage = newInnerPages[currentPageIndex.current];

    if (currentPage.length === 1) {
      if (currentItemIndex.current === items.length - 1) {
        return onReady();
      }

      currentPageIndex.current += 1;
      currentItemIndex.current += 1;

      newInnerPages.push([items[currentItemIndex.current]]);

      return setInnerPages(newInnerPages);
    }

    const lastRemovedBlock = currentPage.pop();
    const [blocksForNewPage, currentPageBlocks] = pageRulesRecursive([lastRemovedBlock], currentPage);
    newInnerPages[currentPageIndex.current] = currentPageBlocks;

    currentPageIndex.current += 1;

    newInnerPages.push(blocksForNewPage);

    setInnerPages(newInnerPages);
  }, [innerPages]);

  const handleReady = useCallback(
    ({ height, maxHeight } = {}) => {
      if (currentItemIndex.current === items.length - 1) {
        return onReady();
      }

      const newInnerPages = clone(innerPages);

      if (
        checkPageDevide({
          currentItemIndex: currentItemIndex.current,
          items,
          height,
          maxHeight,
        })
      ) {
        newInnerPages.push([items[currentItemIndex.current + 1]]);

        currentItemIndex.current += 1;
        currentPageIndex.current += 1;

        setInnerPages(newInnerPages);

        return;
      }

      currentItemIndex.current += 1;
      newInnerPages[currentPageIndex.current].push(items[currentItemIndex.current]);

      setInnerPages(newInnerPages);
    },
    [innerPages],
  );

  const helper = {
    dropLastPage: () => {
      const newInnerPages = [...innerPages];
      newInnerPages.pop();

      setInnerPages(newInnerPages);
    },
  };

  return { innerPages, handleReady, handleFail, helper };
}

export function useLayoutHeight({ maxHeight, ignore, blocks = [], onFail, onReady, depsForRerender }) {
  const ref = useRef(null);

  useEffect(() => {
    if (!ignore && ref.current !== null) {
      const { height } = ref.current.getBoundingClientRect();

      if (height > maxHeight) {
        onFail();
      } else if (height >= 0) {
        onReady({ height, maxHeight });
      }
    }
  }, [blocks.length, depsForRerender]);

  return ref;
}

function isSmallText(text) {
  return !isNil(text) && text.length < 120;
}

function isSmallTextBlock(block) {
  if (!isTextBlock(block)) return false;

  const draftBlocks = block?.data?.raw?.blocks || [];

  return isSmallText(draftBlocks[0]?.text) || isSmallText(block?.data?.title);
}

function isSmallBoldTextBlock(block) {
  if (!isTextBlock(block)) return false;

  const draftBlocks = block?.data?.raw?.blocks || [];
  const isTextBold = draftBlocks.length === 1 && draftBlocks[0].inlineStyleRanges?.[0]?.style === 'BOLD';

  return (isTextBold && isSmallText(draftBlocks[0].text)) || isSmallText(block?.data?.title);
}

function isEmptyTextBlock(block) {
  return block?.data?.text === ' ';
}

function isTextBlock(block) {
  return block?.type === 'text';
}

function pageRulesRecursive(blocksForNewPage, currentPageBlocks, level = 0) {
  blocksForNewPage = [...blocksForNewPage];
  currentPageBlocks = [...currentPageBlocks];

  // to prevent infinite pages generating without blocks
  if (currentPageBlocks.length < 2) {
    return [blocksForNewPage, currentPageBlocks];
  }

  const lastBlock = currentPageBlocks[currentPageBlocks.length - 1];
  const preLastBlock = currentPageBlocks[currentPageBlocks.length - 2];

  // PDF rendering Rules:
  // 1. Page never ends with header type block
  if (lastBlock?.type === 'header') {
    blocksForNewPage.unshift(currentPageBlocks.pop());

    return pageRulesRecursive(blocksForNewPage, currentPageBlocks, level + 1);
  } else if (
    // 2. Page never ends with the text block that looks like a title
    ((isSmallTextBlock(lastBlock) && !isTextBlock(preLastBlock)) || isSmallBoldTextBlock(lastBlock)) &&
    level === 0
  ) {
    blocksForNewPage.unshift(currentPageBlocks.pop());

    return pageRulesRecursive(blocksForNewPage, currentPageBlocks, level + 1);
  } else if (
    // 3. Page never ends with empty line with small text block behind like in Letter: "Dear, Friends!"
    isEmptyTextBlock(lastBlock) &&
    isSmallTextBlock(preLastBlock)
  ) {
    blocksForNewPage.unshift(currentPageBlocks.pop());

    return pageRulesRecursive(blocksForNewPage, currentPageBlocks, level + 1);
  }

  return [blocksForNewPage, currentPageBlocks];
}
